import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Divider
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { getAllReviewTopics, updatePublished } from '../../../../services/mentorServices/ReviewServices';
import { useAttendance } from '../../../../context/AttendanceContext';
import DisplayNotesModal from './notes/DisplayNotesModal';

const LearnerReview = () => {
  const { learner_code } = useParams();
  const navigate = useNavigate();
  const [reviewTopics, setReviewTopics] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedNotesTopic, setSelectedNotesTopic] = useState();
  const { isAttendanceMarked, markAttendanceAction, markingAttendanceId } = useAttendance();

  const fetchReviewTopics = async () => {
    if (!learner_code) {
      localStorage.removeItem('learnerName');
      return;
    }

    try {
      localStorage.removeItem('learnerName');
      const response = await getAllReviewTopics(learner_code);
      setReviewTopics(response.data.data);
      localStorage.setItem('learnerName', response.data.data.learnerDetails.name);
    } catch (error) {
      toast.error('Error fetching review topics');
    }
  };

  useEffect(() => {
    fetchReviewTopics();
  }, [learner_code]);

  const handleNotesClick = (topic) => {
    setSelectedNotesTopic(topic);
    setOpen(true);
  };

  const handleViewClick = (topic) => {
    navigate('../learner/topic-review', {
      state: { topic, learner_code },
    });
  };

  const handlePublishClick = async (topic) => {
    try {
      const response = await updatePublished(
        topic.learner_id,
        topic.makerplanID,
        topic.moduleID,
        topic.topicID,
        { is_published: true }
      );
      toast.success(response?.data?.message || 'Track score published successfully!');
      fetchReviewTopics();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error.message || 'An error occurred while publishing the track score.'
      );
    }
  };

  const handleMarkAttendance = async (reviewTopics) => {
    const learnerId = reviewTopics?.learnerDetails?.learner_id || reviewTopics?.learner_id;
    await markAttendanceAction(learnerId);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', bgcolor: 'background.default' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Learner Topic Review
        </Typography>
        <Button
          variant="contained"
          disabled={isAttendanceMarked(reviewTopics?.learnerDetails?.learner_id) || markingAttendanceId === String(reviewTopics?.learnerDetails?.learner_id)}
          color={isAttendanceMarked(reviewTopics?.learnerDetails?.learner_id) ? "success" : "primary"}
          startIcon={isAttendanceMarked(reviewTopics?.learnerDetails?.learner_id) ? <CheckCircleRoundedIcon sx={{ fontSize: '18px !important' }} /> : null}
          onClick={()=>handleMarkAttendance(reviewTopics)}
          sx={{
            fontWeight: 'bold',
            textTransform: 'capitalize',
            ...(isAttendanceMarked(reviewTopics?.learnerDetails?.learner_id) && {
              '&.Mui-disabled': {
                backgroundColor: '#2e7d32',
                color: '#ffffff',
                opacity: 0.9,
              }
            })
          }}
        >
          {isAttendanceMarked(reviewTopics?.learnerDetails?.learner_id) ? "Attendance Marked" : markingAttendanceId === String(reviewTopics?.learnerDetails?.learner_id) ? "Marking..." : "Mark Attendance"}
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {reviewTopics?.trackScores?.length ? (
        reviewTopics.trackScores.map((topic) => (
          <Card
            key={topic.topicID}
            sx={{
              borderRadius: 3,
              mb: 3,
              boxShadow: 3,
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'scale(1.01)' },
            }}
          >
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Topic:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={500} textAlign="right">
                    {topic.topic_Name || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Review Status:
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  {topic.review_date ? (
                    <CheckCircleRoundedIcon sx={{ color: 'green' }} />
                  ) : (
                    <CloseIcon color="error" />
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Track Score:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={500} textAlign="right">
                    {topic?.track_score || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Review Date:
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography fontWeight={500}>
                    {topic.review_date
                      ? new Date(topic.review_date).toLocaleDateString('en-GB')
                      : '--'}
                  </Typography>
                </Grid>
              </Grid>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'center', md: 'flex-end' },
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                  gap: 1.5,
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => handleNotesClick(topic)}
                  sx={{ fontWeight: 'bold', flex: { xs: '1 1 100%', md: 'unset' } }}
                >
                  {topic?.track_notes_count} Notes
                </Button>

                <Button
                  variant="contained"
                  onClick={() => handleViewClick(topic)}
                  sx={{ fontWeight: 'bold', textTransform: 'capitalize', flex: { xs: '1 1 100%', md: 'unset' } }}
                >
                  Review
                </Button>

                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handlePublishClick(topic)}
                  sx={{ fontWeight: 'bold', textTransform: 'capitalize', flex: { xs: '1 1 100%', md: 'unset' } }}
                  disabled={topic.isPublished === 1}
                >
                  {topic.isPublished === 1 ? 'Published' : 'Publish'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))
      ) : (
        <Typography align="center" color="text.secondary">
          No topics found.
        </Typography>
      )}

      <DisplayNotesModal
        open={open}
        onClose={() => setOpen(false)}
        selectedNotesTopic={selectedNotesTopic}
        fetchReviewTopics={fetchReviewTopics}
      />
    </Box>
  );
};

export default LearnerReview;
